import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import type { AdminReadingTopicDto, AdminReadingTopicsResponseDto } from '@lunary/api-client'
import { ApiError, api } from '../../shared/api/client'

export const READING_TOPICS_KEY = ['admin', 'reading-topics'] as const

export type TopicPatch = { title?: string; hint?: string; enabled?: boolean }
export type NewTopic = { slug: string; title: string; hint: string }

export function topicErrorText(e: unknown): string {
  if (e instanceof ApiError) return e.message
  return 'Не получилось. Проверьте соединение и повторите'
}

export function useReadingTopics() {
  return useQuery({
    queryKey: READING_TOPICS_KEY,
    queryFn: () => api.get<AdminReadingTopicsResponseDto>('/admin/reading-topics'),
    retry: false,
  })
}

function useSync() {
  const qc = useQueryClient()
  return {
    list: (data: AdminReadingTopicsResponseDto) => qc.setQueryData(READING_TOPICS_KEY, data),
    refresh: () => void qc.invalidateQueries({ queryKey: READING_TOPICS_KEY }),
  }
}

export function useCreateTopic() {
  const { refresh } = useSync()
  return useMutation({
    mutationFn: (input: NewTopic) => api.post<AdminReadingTopicDto>('/admin/reading-topics', input),
    onSuccess: refresh,
  })
}

export function useUpdateTopic() {
  const { refresh } = useSync()
  return useMutation({
    mutationFn: (input: { slug: string; patch: TopicPatch }) =>
      api.patch<AdminReadingTopicDto>(`/admin/reading-topics/${encodeURIComponent(input.slug)}`, input.patch),
    onSuccess: refresh,
    onError: refresh,
  })
}

export function useReorderTopics() {
  const qc = useQueryClient()
  const { list, refresh } = useSync()
  return useMutation({
    mutationFn: (slugs: string[]) => api.put<AdminReadingTopicsResponseDto>('/admin/reading-topics/order', { slugs }),
    onMutate: async (slugs) => {
      await qc.cancelQueries({ queryKey: READING_TOPICS_KEY })
      const current = qc.getQueryData<AdminReadingTopicsResponseDto>(READING_TOPICS_KEY)
      if (!current) return
      const bySlug = new Map(current.items.map((t) => [t.slug, t]))
      const items = slugs.flatMap((slug) => bySlug.get(slug) ?? [])
      if (items.length === current.items.length) qc.setQueryData(READING_TOPICS_KEY, { ...current, items })
    },
    onSuccess: list,
    onError: refresh,
  })
}

export function useDeleteTopic() {
  const { refresh } = useSync()
  return useMutation({
    mutationFn: (slug: string) => api.delete<void>(`/admin/reading-topics/${encodeURIComponent(slug)}`),
    onSuccess: refresh,
    onError: refresh,
  })
}
